import { PropTypes } from 'prop-types';
import React, { useImperativeHandle, useRef } from 'react';
import { DragDropProvider, PointerSensor } from '@dnd-kit/react';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { isSortable } from '@dnd-kit/react/sortable';
import { buildMovePayload } from 'utils/reorder';
import * as Ui from './DraggableGridArea.styles';
import SortableItem from './SortableItem';

const DraggableGridArea = React.forwardRef(
  (
    {
      items,
      getItemId,
      getItemGridInfo,
      renderItem,
      onReorder,
      onRemove,
      onEdit,
      canEditItem,
      gridColumns = 3,
    },
    ref,
  ) => {
    const gridAreaRef = useRef(null);

    useImperativeHandle(ref, () => ({
      scrollToItem(id) {
        const element = gridAreaRef.current?.querySelector(`[data-item-id="${id}"]`);
        if (!element) {
          return false;
        }

        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      },
    }));

    const handleDragEnd = (event) => {
      if (!onReorder || event.canceled || event.cancelled) return;

      const { source } = event.operation;

      if (isSortable(source)) {
        const { initialIndex, index } = source;

        if (initialIndex !== index) {
          const move = buildMovePayload(items, initialIndex, index, getItemId);

          if (move) {
            onReorder(move);
          }
        }
      }
    };

    const handleRemove = (id) => {
      onRemove?.(id);
    };

    return (
      <DragDropProvider
        sensors={[PointerSensor]}
        modifiers={[RestrictToElement.configure({ element: () => gridAreaRef.current })]}
        onDragEnd={handleDragEnd}
      >
        <Ui.GridArea ref={gridAreaRef} $gridColumns={gridColumns}>
          {items.map((item, index) => (
            <SortableItem
              key={getItemId(item)}
              id={getItemId(item)}
              index={index}
              item={item}
              getItemGridInfo={getItemGridInfo}
              renderItem={renderItem}
              onRemove={onRemove ? handleRemove : undefined}
              onEdit={onEdit}
              canEdit={canEditItem ? canEditItem(item) : true}
              isDraggable={!!onReorder}
              isVerticalList={gridColumns === 1}
            />
          ))}
        </Ui.GridArea>
      </DragDropProvider>
    );
  },
);

DraggableGridArea.propTypes = {
  items: PropTypes.array.isRequired,
  getItemId: PropTypes.func.isRequired,
  getItemGridInfo: PropTypes.func.isRequired,
  renderItem: PropTypes.func.isRequired,
  onReorder: PropTypes.func,
  onRemove: PropTypes.func,
  onEdit: PropTypes.func,
  canEditItem: PropTypes.func,
  gridColumns: PropTypes.number,
};

DraggableGridArea.displayName = 'DraggableGridArea';

export default DraggableGridArea;
